import React, { useState, useEffect } from 'react';
import { Bug, X, Database, User, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../utils/supabase';
import { useAuth } from '../contexts/AuthContext';

/**
 * Panel de depuración para diagnosticar problemas con Supabase
 * Solo se muestra si se presiona Ctrl + Shift + D
 */
const DebugPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [testResults, setTestResults] = useState({});
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        setIsOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const runTests = async () => {
    const results = {};

    // Test 1: Supabase inicializado
    results.supabaseInit = !!supabase;

    // Test 2: Usuario autenticado
    results.userAuth = !!user && isAuthenticated;

    if (supabase && user) {
      // Test 3: Conectividad a quiz_history
      try {
        const { error: readError } = await supabase
          .from('quiz_history')
          .select('id')
          .limit(1);
        results.quizHistoryRead = !readError;
        results.quizHistoryReadError = readError?.message;
      } catch (err) {
        results.quizHistoryRead = false;
        results.quizHistoryReadError = err.message;
      }

      // Test 4: Intentar INSERT en quiz_history
      try {
        const testData = {
          user_id: user.id,
          score: 999,
          attempts: 1,
          accuracy: 100,
          regions: ['test'],
          difficulties: ['test'],
          question_types: ['test']
        };

        const { data, error: insertError } = await supabase
          .from('quiz_history')
          .insert(testData)
          .select();

        results.quizHistoryInsert = !insertError;
        results.quizHistoryInsertError = insertError?.message;
        results.quizHistoryInsertCode = insertError?.code;
        results.quizHistoryInsertHint = insertError?.hint;

        // Limpiar registro de prueba
        if (data && data[0]) {
          await supabase.from('quiz_history').delete().eq('id', data[0].id);
        }
      } catch (err) {
        results.quizHistoryInsert = false;
        results.quizHistoryInsertError = err.message;
      }

      // Test 5: Conectividad a user_achievements
      try {
        const { error: achievReadError } = await supabase
          .from('user_achievements')
          .select('*')
          .limit(1);
        results.achievementsRead = !achievReadError;
        results.achievementsReadError = achievReadError?.message;
      } catch (err) {
        results.achievementsRead = false;
        results.achievementsReadError = err.message;
      }
    }

    setTestResults(results);
  };

  useEffect(() => {
    if (isOpen) {
      runTests();
    }
  }, [isOpen, user]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bug className="w-6 h-6 text-purple-600" />
            <h2 className="text-xl font-bold text-gray-800">Panel de Depuración</h2>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Estado de Supabase */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              Estado de Supabase
            </h3>
            <TestResult
              label="Supabase Inicializado"
              passed={testResults.supabaseInit}
            />
          </div>

          {/* Estado de Autenticación */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <User className="w-5 h-5 text-green-600" />
              Autenticación
            </h3>
            <TestResult
              label="Usuario Autenticado"
              passed={testResults.userAuth}
            />
            {user && (
              <div className="mt-2 text-sm text-gray-600">
                <p><strong>ID:</strong> {user.id}</p>
                <p><strong>Email:</strong> {user.email}</p>
              </div>
            )}
          </div>

          {/* Tests de Base de Datos */}
          {supabase && user && (
            <>
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-bold text-gray-800 mb-3">Quiz History</h3>
                <TestResult
                  label="Lectura (SELECT)"
                  passed={testResults.quizHistoryRead}
                  error={testResults.quizHistoryReadError}
                />
                <TestResult
                  label="Escritura (INSERT)"
                  passed={testResults.quizHistoryInsert}
                  error={testResults.quizHistoryInsertError}
                  code={testResults.quizHistoryInsertCode}
                  hint={testResults.quizHistoryInsertHint}
                />
              </div>

              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-bold text-gray-800 mb-3">User Achievements</h3>
                <TestResult
                  label="Lectura (SELECT)"
                  passed={testResults.achievementsRead}
                  error={testResults.achievementsReadError}
                />
              </div>
            </>
          )}

          {/* Botón para re-ejecutar tests */}
          <button
            onClick={runTests}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-bold transition-colors"
          >
            Ejecutar Tests Nuevamente
          </button>

          {/* Información adicional */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm">
            <p className="font-bold text-blue-900 mb-2">💡 Cómo usar:</p>
            <ul className="text-blue-800 space-y-1 list-disc list-inside">
              <li>Presiona <kbd className="px-2 py-1 bg-white rounded">Ctrl + Shift + D</kbd> para mostrar/ocultar</li>
              <li>Verifica que todos los tests pasen (✓)</li>
              <li>Si algo falla (✗), revisa el error mostrado</li>
              <li>Errores comunes: RLS bloqueando INSERT, usuario no autenticado</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const TestResult = ({ label, passed, error, code, hint }) => {
  if (passed === undefined) {
    return (
      <div className="flex items-center gap-2 py-2">
        <div className="w-5 h-5 rounded-full bg-gray-300 animate-pulse" />
        <span className="text-gray-600">{label}: Ejecutando...</span>
      </div>
    );
  }

  return (
    <div className="py-2">
      <div className="flex items-center gap-2">
        {passed ? (
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
        ) : (
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
        )}
        <span className={passed ? 'text-green-800 font-semibold' : 'text-red-800 font-semibold'}>
          {label}: {passed ? 'OK' : 'FALLO'}
        </span>
      </div>
      {!passed && error && (
        <div className="ml-7 mt-1 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
          <p><strong>Error:</strong> {error}</p>
          {code && <p><strong>Código:</strong> {code}</p>}
          {hint && <p><strong>Sugerencia:</strong> {hint}</p>}
        </div>
      )}
    </div>
  );
};

export default DebugPanel;
